import React, { Suspense } from 'react'

import { TypedDocumentNode, useSuspenseQuery } from '@apollo/client'
import { Dashboard } from '@factorialco/f0-react/dist/experimental'
import { PermissionsPolicyResolution } from 'generated/resources/graphql'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import {
  ExtractCommonWidgetArgs,
  NoQueryWidgetParams,
  Widget,
  WidgetParams,
} from 'modules/core/lib/widgets'

const allowedTo = (
  policiesData: Pick<PermissionsPolicyResolution, 'id' | 'policy' | 'can'>[],
  item: string
) => {
  return policiesData.find((policy) => policy.policy === item)?.can ?? false
}

function DashboardContainer<TWidgets extends ReadonlyArray<WidgetParams | NoQueryWidgetParams>>({
  widgets,
  args,
}: Readonly<{
  widgets: TWidgets | ReadonlyArray<WidgetParams | NoQueryWidgetParams>
  employeeId: number
  args: ExtractCommonWidgetArgs<TWidgets>
}>) {
  // new widgets
  return (
    <Suspense fallback={<Dashboard.Skeleton />}>
      <Dashboard>
        {widgets.map((widget) => (
          <ErrorBoundary key={widget.id} customCrashComponent={<></>}>
            <Widget widget={widget} args={args} useSuspense />
          </ErrorBoundary>
        ))}
      </Dashboard>
    </Suspense>
  )
}

function WidgetDashboardWithPolicies<
  TWidgets extends ReadonlyArray<WidgetParams | NoQueryWidgetParams>,
  PoliciesQuery,
  PoliciesVariables = { policies: PermissionsPolicyResolution[]; employeeId: number }
>({
  widgets,
  args,
  employeeId,
  policiesQuery,
  extractPolicies,
}: Readonly<{
  widgets: TWidgets | ReadonlyArray<WidgetParams | NoQueryWidgetParams>
  args: ExtractCommonWidgetArgs<TWidgets | ReadonlyArray<WidgetParams | NoQueryWidgetParams>>
  employeeId: number
  policiesQuery: TypedDocumentNode<PoliciesQuery, PoliciesVariables>
  extractPolicies: (
    data: PoliciesQuery
  ) => Pick<PermissionsPolicyResolution, 'id' | 'policy' | 'can'>[]
  children?: React.ReactNode[]
}>) {
  const allPolicies = widgets.flatMap((widget) => widget.policies || [])

  const { data: policiesData } = useSuspenseQuery(policiesQuery, {
    variables: { policies: allPolicies, employeeId },
  })

  const policies = policiesData && extractPolicies ? extractPolicies(policiesData) : []

  const filteredWidgets = policies
    ? widgets.filter((widget) => {
        if (widget.policies === undefined) return true
        return widget.policies.every((policy) => allowedTo(policies, policy))
      })
    : widgets

  return DashboardContainer({ widgets: filteredWidgets, employeeId, args })
}

export function WidgetDashboard<
  TWidgets extends ReadonlyArray<WidgetParams | NoQueryWidgetParams>,
  PoliciesQuery,
  PoliciesVariables = { policies: PermissionsPolicyResolution[]; employeeId: number }
>({
  widgets,
  employeeId,
  args,
  policiesQuery,
  extractPolicies,
}: Readonly<{
  widgets: TWidgets
  employeeId: number
  args: ExtractCommonWidgetArgs<TWidgets>
  policiesQuery?: TypedDocumentNode<PoliciesQuery, PoliciesVariables>
  extractPolicies?: (
    data: PoliciesQuery
  ) => Pick<PermissionsPolicyResolution, 'id' | 'policy' | 'can'>[]
  children?: React.ReactNode[]
}>) {
  return policiesQuery && extractPolicies
    ? WidgetDashboardWithPolicies({ widgets, args, employeeId, policiesQuery, extractPolicies })
    : DashboardContainer({ widgets, employeeId, args })
}
